import type { HTTPError } from '@core/utils/types/errors';
import type { ErrorInfo, ReactNode, ReactElement } from 'react';

import * as Sentry from '@sentry/browser';
import { cloneElement, Component } from 'react';

import Alert from '@ui/Alert';

interface Props {
  apiError?: HTTPError<{ detail: string }> | null;
  body?: string | null;
  children?: ReactNode;
  errorComponent?: ReactElement;
}

interface State {
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  state: State = {};

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    Sentry.captureException(error, {
      tags: { source: 'rdmd-error-boundary' },
      extra: { componentStack: info.componentStack, body: this.props.body },
    });
  }

  fullMessage() {
    if (this.state.error) {
      return <pre>{this.state.error.stack}</pre>;
    } else if (this.props.apiError) {
      return <pre>{this.props.apiError.info?.detail}</pre>;
    }

    return '';
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.error && ('body' in prevProps && prevProps.body) !== ('body' in this.props && this.props.body)) {
      // oxlint-disable-next-line react/no-did-update-set-state
      this.setState({ error: undefined });
    }
  }

  render() {
    const { error } = this.state;
    const { children, apiError, errorComponent } = this.props;

    if (error || apiError) {
      if (errorComponent) {
        // Clone the element to inject the error prop if it's not already provided
        return cloneElement(errorComponent, {
          error: error || undefined,
          ...errorComponent.props,
        });
      }

      return <Alert subtitle={this.fullMessage()} title="The component preview encountered an error!" type="danger" />;
    }

    return children;
  }
}

export default ErrorBoundary;
